import { BugReport, Email, Forum, MenuBook } from '@mui/icons-material';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { FC } from 'react';
import { DocSection } from '../docs-components';

const SupportCard: FC<{ icon: React.ReactNode; title: string; description: string; link: string; linkLabel: string }> = ({
  icon,
  title,
  description,
  link,
  linkLabel
}) => (
  <Card variant="outlined" sx={{ flex: '1 1 260px' }}>
    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {description}
      </Typography>
      <a href={link} target="_blank" rel="noreferrer">
        <Typography variant="body2" color="primary">
          {linkLabel} →
        </Typography>
      </a>
    </CardContent>
  </Card>
);

const Support: FC = () => (
  <Box>
    <Typography variant="h4" fontWeight={700} gutterBottom>
      Support
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      Need help? We offer several support channels depending on the nature of your question.
    </Typography>

    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
      <SupportCard
        icon={<MenuBook fontSize="small" />}
        title="Documentation"
        description="Start here. This documentation covers authentication, all endpoints, error handling, and guides."
        link="#"
        linkLabel="Browse the docs"
      />
      <SupportCard
        icon={<Forum fontSize="small" />}
        title="Developer Forum"
        description="Ask questions, share integrations, and get help from the Thermia developer community."
        link="https://community.thermia.com/developers"
        linkLabel="Visit the forum"
      />
      <SupportCard
        icon={<BugReport fontSize="small" />}
        title="Bug Reports"
        description="Found a bug in the API or portal? Report it via our issue tracker and we'll investigate."
        link="https://github.com/thermia/developer-portal/issues"
        linkLabel="Open an issue"
      />
      <SupportCard
        icon={<Email fontSize="small" />}
        title="Direct Support"
        description="For urgent production issues or account-related questions, contact our developer support team."
        link="mailto:developer-support@thermia.com"
        linkLabel="Email us"
      />
    </Box>

    <DocSection title="Before You Reach Out">
      <Typography variant="body1" gutterBottom>
        To help us resolve your issue as quickly as possible, please include the following information:
      </Typography>
      <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.75 } }}>
        <li>
          <Typography variant="body1">
            The <code>requestId</code> from any error responses.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">The full HTTP request including headers (redact your API key).</Typography>
        </li>
        <li>
          <Typography variant="body1">The full HTTP response body.</Typography>
        </li>
        <li>
          <Typography variant="body1">The API version you are using.</Typography>
        </li>
        <li>
          <Typography variant="body1">A description of what you expected to happen versus what actually happened.</Typography>
        </li>
      </Box>
    </DocSection>

    <DocSection title="SLA">
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': {
            textAlign: 'left',
            padding: '8px 12px',
            borderBottom: '1px solid',
            borderColor: 'divider',
            typography: 'body2'
          },
          '& th': { fontWeight: 700, bgcolor: 'action.hover' }
        }}>
        <thead>
          <tr>
            <th>Priority</th>
            <th>Criteria</th>
            <th>Response Target</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['P1 — Critical', 'API completely unavailable or data loss', '2 hours'],
            ['P2 — High', 'Major functionality broken with no workaround', '1 business day'],
            ['P3 — Normal', 'Minor issues or questions', '3 business days']
          ].map(([priority, criteria, response]) => (
            <tr key={priority}>
              <td>{priority}</td>
              <td>{criteria}</td>
              <td>{response}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </DocSection>
  </Box>
);

export default Support;
